import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  alpha,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  Save,
  RestartAlt,
  Add,
  Delete,
} from '@mui/icons-material';
import axios from 'axios';
import { GlassmorphicCard, PrimaryButton } from '../styles/sharedStyles';

const DEFAULT_SIDEBAR_ORDER = [
  'dashboard',
  'oralb',
  'immich',
  'jellyfin',
  'home_assistant',
  'polish_tracker',
  'nasdrive',
   'unifi',
  'tax',
  'nginx',
  'servers',
  'settings',
];

const LABEL_KEYS = {
  dashboard: 'sidebar.dashboard',
  oralb: 'sidebar.oralb',
  immich: 'sidebar.immich',
  jellyfin: 'sidebar.jellyfin',
  home_assistant: 'sidebar.home_assistant',
  polish_tracker: 'sidebar.polish_tracker',
  nasdrive: 'sidebar.nas_drive',
  unifi: 'sidebar.unifi',
  tax: 'sidebar.tax',
  nginx: 'Nginx Proxy Manager',
  servers: 'sidebar.servers',
  settings: 'sidebar.settings',
};

function isItemVisible(itemId, allowedTabs, isAdmin, isTrueNASOffline) {
  const allowed = allowedTabs ?? [];
  switch (itemId) {
    case 'dashboard':
      return true;
    case 'oralb':
      return isAdmin || allowed.includes('oralb');
    case 'immich':
      return !isTrueNASOffline && (isAdmin || ['immich_statistics', 'immich_adam_library', 'immich_jobs', 'immich_users', 'immich_libraries', 'immich_nightly_tasks', 'immich_about'].some((tab) => allowed.includes(tab)));
    case 'jellyfin':
      return !isTrueNASOffline && (isAdmin || allowed.includes('jellyfin'));
    case 'home_assistant':
      return isAdmin || ['home_assistant', 'home_assistant_automations', 'home_assistant_room_designer', 'home_assistant_backups'].some((tab) => allowed.includes(tab));
    case 'polish_tracker':
      return isAdmin || allowed.includes('polish_tracker');
    case 'nasdrive':
      return !isTrueNASOffline && (isAdmin || allowed.includes('nas_drive'));
    case 'unifi':
      return isAdmin || allowed.some((tab) => tab === 'unifi' || tab.startsWith('unifi_'));
    case 'tax':
      return isAdmin || allowed.includes('tax');
    case 'nginx':
      return isAdmin || allowed.includes('nginx_proxy_manager');
    case 'servers':
      return isAdmin || ['truenas_services', 'services_truenas', 'services_rpi5', 'debian_sysmon', 'services_ha', 'truenas_cron_jobs', 'truenas_apps', 'truenas_virtual_machines', 'truenas_storage_pools', 'truenas_data_protection', 'task_scheduler'].some((tab) => allowed.includes(tab));
    case 'settings':
      return isAdmin || ['docker_maintenance', 'docker_security', 'chat', 'game_snake', 'game_server_ping', 'game_disk_whack'].some((tab) => allowed.includes(tab));
    default:
      return false;
  }
}

function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || 'group';
}

/** Convert item-only order to mixed order (group ids replace runs of group items). */
function itemOrderToMixedOrder(itemOrder, groups) {
  const itemToGroup = {};
  groups.forEach((g) => {
    (g.itemIds || []).forEach((id) => { itemToGroup[id] = g; });
  });
  const mixed = [];
  const addedGroups = new Set();
  itemOrder.forEach((id) => {
    const g = itemToGroup[id];
    if (!g) {
      mixed.push(id);
      return;
    }
    if (!addedGroups.has(g.id)) {
      addedGroups.add(g.id);
      mixed.push(g.id);
    }
  });
  return mixed;
}

export default function SidebarSettings({ apiBaseUrl, token, userInfo, isTrueNASOffline = false, onSave }) {
  const { t, i18n } = useTranslation();
  const allowedTabs = userInfo?.allowed_tabs ?? [];
  const isAdmin = userInfo?.is_admin ?? false;

  const visibleIds = useMemo(() => {
    return DEFAULT_SIDEBAR_ORDER.filter((id) => isItemVisible(id, allowedTabs, isAdmin, isTrueNASOffline));
  }, [allowedTabs, isAdmin, isTrueNASOffline]);

  const [order, setOrder] = useState([]);
  const [groups, setGroups] = useState([]);
  const [newGroupLabel, setNewGroupLabel] = useState('');
  const [newGroupItemIds, setNewGroupItemIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const config = userInfo?.sidebar_config;
    const groupList = config?.groups && Array.isArray(config.groups)
      ? config.groups.map((g) => ({
          id: g.id || slugify(g.label || ''),
          label: g.label || '',
          itemIds: Array.isArray(g.itemIds) ? g.itemIds.filter((id) => visibleIds.includes(id)) : [],
        }))
      : [];
    setGroups(groupList);

    if (config && config.order && Array.isArray(config.order)) {
      const groupIds = new Set(groupList.map((g) => g.id));
      const hasGroupIds = config.order.some((id) => groupIds.has(id));
      if (hasGroupIds) {
        const groupedItemIds = new Set(
          groupList.flatMap((g) => g.itemIds || [])
        );
        const filtered = [];

        // Keep explicit group entries and standalone items
        config.order.forEach((id) => {
          if (groupIds.has(id)) {
            if (!filtered.includes(id)) filtered.push(id);
          } else if (!groupedItemIds.has(id) && visibleIds.includes(id)) {
            if (!filtered.includes(id)) filtered.push(id);
          }
        });

        // Append any new standalone items not yet represented
        visibleIds.forEach((id) => {
          if (!groupedItemIds.has(id) && !filtered.includes(id)) {
            filtered.push(id);
          }
        });

        setOrder(filtered);
      } else {
        const validOrder = config.order.filter((id) => DEFAULT_SIDEBAR_ORDER.includes(id));
        const visibleOrder = visibleIds.filter((id) => validOrder.includes(id));
        const missing = visibleIds.filter((id) => !validOrder.includes(id));
        const raw = [...visibleOrder, ...missing];
        setOrder(groupList.length > 0 ? itemOrderToMixedOrder(raw, groupList) : raw);
      }
    } else {
      setOrder([...visibleIds]);
    }
  }, [userInfo?.sidebar_config, visibleIds]);

  const moveUp = (index) => {
    if (index <= 0) return;
    const next = [...order];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    setOrder(next);
  };

  const moveDown = (index) => {
    if (index >= order.length - 1) return;
    const next = [...order];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    setOrder(next);
  };

  const addGroup = () => {
    const label = newGroupLabel.trim();
    if (!label || newGroupItemIds.length === 0) return;
    const id = slugify(label);
    const newId = groups.some((g) => g.id === id) ? `${id}_${Date.now()}` : id;
    setGroups((prev) => [
      ...prev,
      { id: newId, label, itemIds: [...newGroupItemIds] },
    ]);
    setOrder((prev) => {
      const indices = newGroupItemIds.map((itemId) => prev.indexOf(itemId)).filter((i) => i >= 0);
      if (indices.length === 0) return [...prev, newId];
      const minIdx = Math.min(...indices);
      const next = prev.filter((x) => !newGroupItemIds.includes(x));
      next.splice(minIdx, 0, newId);
      return next;
    });
    setNewGroupLabel('');
    setNewGroupItemIds([]);
  };

  const removeGroup = (groupId) => {
    const group = groups.find((g) => g.id === groupId);
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
    if (group && Array.isArray(group.itemIds)) {
      setOrder((prev) =>
        prev.flatMap((id) => (id === groupId ? group.itemIds : [id]))
      );
    }
  };

  const moveGroupItemUp = (groupId, index) => {
    if (index <= 0) return;
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId || !Array.isArray(g.itemIds)) return g;
        const ids = [...g.itemIds];
        [ids[index - 1], ids[index]] = [ids[index], ids[index - 1]];
        return { ...g, itemIds: ids };
      })
    );
  };

  const moveGroupItemDown = (groupId, index) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId || !Array.isArray(g.itemIds)) return g;
        const ids = [...g.itemIds];
        if (index >= ids.length - 1) return g;
        [ids[index], ids[index + 1]] = [ids[index + 1], ids[index]];
        return { ...g, itemIds: ids };
      })
    );
  };

  const resetToDefault = () => {
    setOrder([...visibleIds]);
    setGroups([]);
    setError(null);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    setSaving(true);
    try {
      const language = i18n?.language || 'en';
      await axios.post(
        `${apiBaseUrl}/api/user/preferences`,
        {
          language,
          sidebar_config: {
            order: [...order],
            groups: groups.map((g) => ({ id: g.id, label: g.label, itemIds: g.itemIds })),
            collapsedGroups: userInfo?.sidebar_config?.collapsedGroups || [],
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      if (typeof onSave === 'function') onSave();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (order.length === 0 && visibleIds.length > 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>Loading...</Typography>
      </Box>
    );
  }

  const getEntryLabel = (id) => {
    const g = groups.find((x) => x.id === id);
    return g ? g.label : t(LABEL_KEYS[id] || id);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(false)} sx={{ mb: 2 }}>
          Sidebar settings saved.
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
        Reorder items and groups (drag order is used in sidebar)
      </Typography>
      <GlassmorphicCard sx={{ p: 2, mb: 3 }}>
        <List dense disablePadding>
          {order.map((id, index) => (
            <ListItem
              key={id}
              secondaryAction={
                <Box>
                  <IconButton size="small" onClick={() => moveUp(index)} disabled={index === 0} sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => moveDown(index)} disabled={index === order.length - 1} sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                </Box>
              }
              sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', '&:last-of-type': { borderBottom: 'none' } }}
            >
              <ListItemText
                primary={getEntryLabel(id)}
                secondary={groups.some((g) => g.id === id) ? 'Group' : null}
                primaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', fontWeight: groups.some((g) => g.id === id) ? 600 : 400 } }}
                secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem' } }}
              />
            </ListItem>
          ))}
        </List>
      </GlassmorphicCard>

      <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
        Group headers (optional)
      </Typography>
      <GlassmorphicCard sx={{ p: 2, mb: 3 }}>
        {groups.length > 0 && (
          <List dense sx={{ mb: 2 }}>
            {groups.map((g) => (
              <Box key={g.id} sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', pb: 1.5, mb: 1.5 }}>
                <ListItem
                  secondaryAction={
                    <IconButton size="small" onClick={() => removeGroup(g.id)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  }
                  sx={{ py: 0.5 }}
                >
                  <ListItemText
                    primary={g.label}
                    primaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.9)', fontWeight: 600 } }}
                  />
                </ListItem>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', px: 2, mb: 0.5 }}>
                  Order inside group:
                </Typography>
                <List dense disablePadding sx={{ pl: 1 }}>
                  {g.itemIds.map((id, idx) => (
                    <ListItem
                      key={id}
                      secondaryAction={
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => moveGroupItemUp(g.id, idx)}
                            disabled={idx === 0}
                            sx={{ color: 'rgba(255,255,255,0.6)' }}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveGroupItemDown(g.id, idx)}
                            disabled={idx === g.itemIds.length - 1}
                            sx={{ color: 'rgba(255,255,255,0.6)' }}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                      sx={{ borderBottom: '1px solid rgba(255,255,255,0.04)', minHeight: 36, '&:last-of-type': { borderBottom: 'none' } }}
                    >
                      <ListItemText
                        primary={t(LABEL_KEYS[id] || id)}
                        primaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </List>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
          <TextField
            size="small"
            placeholder="Group label (e.g. Applications)"
            value={newGroupLabel}
            onChange={(e) => setNewGroupLabel(e.target.value)}
            sx={{ minWidth: 180, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Items:
          </Typography>
          {visibleIds.map((id) => (
            <Button
              key={id}
              size="small"
              variant={newGroupItemIds.includes(id) ? 'contained' : 'outlined'}
              onClick={() => setNewGroupItemIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))}
              sx={{ minWidth: 'auto', fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.3)' }}
            >
              {t(LABEL_KEYS[id] || id)}
            </Button>
          ))}
          <Button size="small" startIcon={<Add />} onClick={addGroup} disabled={!newGroupLabel.trim() || newGroupItemIds.length === 0} sx={{ color: (theme) => theme.palette.primary.main }}>
            Add group
          </Button>
        </Box>
      </GlassmorphicCard>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <PrimaryButton startIcon={<RestartAlt />} onClick={resetToDefault}>
          Reset to default
        </PrimaryButton>
        <PrimaryButton startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </PrimaryButton>
      </Box>
    </Box>
  );
}

export { DEFAULT_SIDEBAR_ORDER, isItemVisible, LABEL_KEYS };
